/**
 * This file is part of ONP.
 *
 * Copyright (c) 2013 Opensoft (http://opensoftdev.com)
 *
 * The unauthorized use of this code outside the boundaries of
 * Opensoft is prohibited.
 *
 * @author Andrey Tkachenko <andrey.tkachenko@opensoftdev.ru>
 */

define(['backbone', 'assets/js/modules/design/designFaceProcessModel', 'assets/js/modules/option/optionValueModel'], function(
    backbone,
    DesignFaceProcessModel,
    OptionValueModel
) {
    var Operation;

    var DEFAULT_TIMEOUT = 10000;

    return (Operation = backbone.Model.extend(
        {
            defaults: {
                name: '',
                progress: 0,
                message: '',
                object: null,
                status: 0
            },

            _context: null,
            _options: null,
            _handle: null,
            _timeout: null,

            initialize: function(attrs, options, timeout) {
                this._options = options || {};
                this._timeout = timeout === undefined ? DEFAULT_TIMEOUT : timeout;
            },

            __begin: function() {
                var self = this;
                if (this._timeout) {
                    this._handle = setTimeout(function() {
                        self.setFinished(Operation.ERROR, 'time out');
                    }, this._timeout);
                }
            },

            __end: function() {
                clearTimeout(this._handle);
            },

            terminate: function() {
                if (this._options && this._options.terminate) {
                    this._options.terminate.apply(this, arguments);
                    this.setFinished(Operation.TERMINATED);
                }
            },

            stop: function() {
                if (this._options && this._options.stop) {
                    this._options.stop.apply(this, arguments);
                    this.setStopped();
                }

                return this;
            },

            start: function(object) {
                if (object) {
                    this.set('object', object);
                }

                if (this._options && this._options.start) {
                    this._options.start.apply(this, arguments);
                    this.setStarted();
                }

                return this;
            },

            resume: function() {
                if (this.get('status') == Operation.STOPPED) {
                    if (this._options && this._options.resume) {
                        this._options.resume.apply(this, arguments);
                        this.setStarted();
                    }
                }

                return this;
            },

            setStarted: function(message) {
                this.set('progress', 0);
                if (message) {
                    this.set('message', message);
                }
                this.set('status', Operation.IN_PROGRESS);

                this.__begin();

                this.trigger(Operation.EVENT_STARTED, this.get('object'), this.get('progress'));
            },

            setStopped: function(message) {
                this.set('status', Operation.STOPPED);
                this.set('message', message || 'stopped');

                this.__end();

                this.trigger(Operation.EVENT_STOPPED, this.get('object'), this.get('progress'));
            },

            setFinished: function(status, value, message) {
                this.set('progress', 100);
                this.set('message', message || this.getStatusMessage(status));
                this.set('status', status);
                this.__end();

                if (status == Operation.SUCCESS) {
                    this.trigger(Operation.EVENT_SUCCESS, value, this.get('message'));
                }

                if (status == Operation.ERROR) {
                    this.trigger(Operation.EVENT_ERROR, value, this.get('message'));
                }

                if (status == Operation.TERMINATED) {
                    this.trigger(Operation.EVENT_TERMINATED, value, this.get('message'));
                }

                this.trigger(Operation.EVENT_FINISHED, value, status, this.get('message'));
            },

            setProgress: function(progress, message) {
                this.set('progress', progress);
                if (message) {
                    this.set('message', message);
                }

                this.trigger(Operation.EVENT_PROGRESS, this.get('object'), this.get('progress'), this.get('message'));
            },

            getProgress: function() {
                var status = this.get('status');
                if (status == Operation.SUCCESS || status == Operation.TERMINATED || status == Operation.ERROR) {
                    return 100;
                } else {
                    return this.get('progress');
                }
            },

            getStatusMessage: function(status) {
                return status == Operation.AWAITING
                    ? 'not started'
                    : status == Operation.SUCCESS
                      ? 'done'
                      : status == Operation.STOPPED
                        ? 'stopped'
                        : status == Operation.IN_PROGRESS
                          ? 'in progress'
                          : status == Operation.TERMINATED ? 'terminated' : status == Operation.ERROR ? 'error' : 'undefined';
            },

            setContext: function(context) {
                this._options.start = context.start || this._options.start;
                this._options.resume = context.resume || this._options.resume;
                this._options.stop = context.stop || this._options.stop;
                this._options.terminate = context.terminate || this._options.terminate;
            },

            getContext: function() {
                return {
                    start: this._options.start,
                    resume: this._options.resume,
                    stop: this._options.stop,
                    terminate: this._options.terminate
                };
            },

            getStatus: function() {
                return this.get('status');
            },

            getMessage: function() {
                return this.get('message') || this.getStatusMessage(this.get('status'));
            },

            getObject: function() {
                return this.get('object');
            },

            setObject: function(object) {
                this.set('object', object);
            },

            mirrorFor: function(operation) {
                this.setContext(operation.getContext());

                this.listenTo(operation, Operation.EVENT_PROGRESS, function(value, progress, message) {
                    this.setProgress(progress, message);
                });

                this.listenTo(operation, Operation.EVENT_FINISHED, function(value, message) {
                    this.setFinished(operation.getStatus(), value, message);
                });

                this.listenTo(operation, Operation.EVENT_STARTED, function(value, message) {
                    this.setStarted(message);
                });

                this.listenTo(operation, Operation.EVENT_STOPPED, function(value, message) {
                    this.setStarted(message);
                });
            },

            isInProgress: function() {
                return this.getStatus() == Operation.IN_PROGRESS;
            },

            toString: function() {
                var progress;

                progress = this.getProgress() ? ' [' + this.getProgress() + ']' : '';

                return this.get('name') + ': ' + this.getMessage() + progress;
            },

            /**
             * @returns {Array|null}
             */
            getFaces: function() {
                const faces = this.getObject().faces;

                if (!faces || !Array.isArray(faces)) {
                    return null;
                }

                const foilProcesses = new DesignFaceProcessModel().getFoilProcesses();

                return faces.map(face => ({
                    ...face,
                    processes: face.processes.map(process => ({
                        ...process,
                        type: foilProcesses.includes(process.type) ? DesignFaceProcessModel.TYPE_FOIL : process.type,
                        subType: foilProcesses.includes(process.type) ? process.type : null
                    }))
                }));
            },

            /**
             * @returns {Object}
             */
            getFacesFoil: function() {
                const facesFoil = { frontFoilColor: undefined, backFoilColor: undefined };
                const faces = this.getObject().faces;

                if (!faces || !Array.isArray(faces)) {
                    return facesFoil;
                }

                faces.forEach(({ faceType, processes }) =>
                    processes.forEach(({ type }) => {
                        if (faceType === 'front') {
                            switch (type) {
                                case DesignFaceProcessModel.TYPE_FOIL_SILVER:
                                    facesFoil.frontFoilColor = OptionValueModel.NAME_FRONT_FOIL_SILVER;
                                    break;
                                case DesignFaceProcessModel.TYPE_FOIL_GOLD:
                                    facesFoil.frontFoilColor = OptionValueModel.NAME_FRONT_FOIL_GOLD;
                                    break;
                            }
                        }

                        if (faceType === 'back') {
                            switch (type) {
                                case DesignFaceProcessModel.TYPE_FOIL_SILVER:
                                    facesFoil.backFoilColor = OptionValueModel.NAME_BACK_FOIL_SILVER;
                                    break;
                                case DesignFaceProcessModel.TYPE_FOIL_GOLD:
                                    facesFoil.backFoilColor = OptionValueModel.NAME_BACK_FOIL_GOLD;
                                    break;
                            }
                        }
                    })
                );

                return facesFoil;
            }
        },
        {
            AWAITING: 0,
            SUCCESS: 1,
            STOPPED: 2,
            IN_PROGRESS: 3,
            TERMINATED: 4,
            ERROR: 5,

            EVENT_STARTED: 'started',
            EVENT_STOPPED: 'stopped',
            EVENT_PROGRESS: 'progress',
            EVENT_FINISHED: 'finished',
            EVENT_TERMINATED: 'terminated',
            EVENT_ERROR: 'error',
            EVENT_SUCCESS: 'success'
        }
    ));
});
